import {
  FORM_CLOSE_EVENT,
  GLOBAL_LANGUAGE_KEY,
  GLOBAL_METADATA_KEY,
  GLOBAL_STRUCTURE_KEY,
  IFeedbackFormMetadata,
  IFeedbackFormStructure,
  IFrameCloseEvent,
} from '@wix/feedback-loop-structure';
import { enterSlideUpFadeIn, exitFadeOut } from './animations';
import { positions } from './positions';
import { notifyFormShown } from './api';

const IFRAME_MARKER = 'feedback-loop-iframe-instance';
export const SHADOW_SIZE = 16;

function generateFormIframeContent({
  structure,
  metadata,
  language,
}: {
  structure: IFeedbackFormStructure;
  metadata: IFeedbackFormMetadata;
  language: string;
}) {
  /**
   * TODO: somehow override it for local development
   */
  const base =
    'https://static.parastorage.com/unpkg-semver/feedback-loop-ui@latest';

  const scriptUrl = `${base}/feedback-loop-ui.umd.js`;
  const cssUrl = `${base}/style.css`;

  return `
<html>
  <head>
    <link rel="stylesheet" type="text/css" href="${cssUrl}">
    <script>
      globalThis.${GLOBAL_STRUCTURE_KEY} = JSON.parse('${JSON.stringify(
    structure,
  )}');
      globalThis.${GLOBAL_METADATA_KEY} = JSON.parse('${JSON.stringify(
    metadata,
  )}');
      globalThis.${GLOBAL_LANGUAGE_KEY} = JSON.parse('${JSON.stringify(
    language,
  )}');
    </script>

    <style>
      body {
        margin: 0;
        padding: 0;
        display: flex;
        align-items: flex-end;
      }
    </style>
  </head>
  <body>
    <div id="root" style="padding: ${SHADOW_SIZE}px;"></div>
    <script src="${scriptUrl}"></script>
  </body>
</html>
`;
}

function createIframe(position = positions.default) {
  const iframe = document.createElement('iframe');
  iframe.id = IFRAME_MARKER;

  // reset
  iframe.style.background = 'transparent';
  iframe.style.border = 'none';

  // positions
  iframe.style.position = 'fixed';

  iframe.style.width = '392px';
  iframe.style.height = '0px';
  iframe.style.zIndex = '9999';

  enterSlideUpFadeIn.setupInitialStyles(iframe.style);

  const rect = position();
  Object.keys(rect).forEach((id) => {
    // @ts-expect-error
    iframe.style[id] = rect[id];
  });

  return iframe;
}

export class FeedbackLoopFrameInjector {
  #pendingIframeResolver?: (event: IFrameCloseEvent) => null;

  static isFeedbackFormIframeVisible() {
    return document.querySelectorAll(`#${IFRAME_MARKER}`).length !== 0;
  }

  static getCurrentFeedbackLoopIframe() {
    return document.querySelector(`#${IFRAME_MARKER}`) as HTMLIFrameElement;
  }

  open(
    structure: IFeedbackFormStructure,
    metadata: IFeedbackFormMetadata,
    position = positions.default,
    language: string,
  ): Promise<
    | IFrameCloseEvent
    | {
        reason: 'not-shown';
      }
  > {
    if (
      typeof window !== 'undefined' &&
      !FeedbackLoopFrameInjector.isFeedbackFormIframeVisible()
    ) {
      this.#injectFeedbackFormIframe(structure, metadata, position, language);

      return new Promise((resolve) => {
        // @ts-expect-error
        this.#pendingIframeResolver = resolve;
      });
    }

    return Promise.resolve({
      reason: 'not-shown',
    });
  }

  show() {
    // TODO: implement show, with correct animations
    const iframe = FeedbackLoopFrameInjector.getCurrentFeedbackLoopIframe();

    if (iframe) {
      iframe.style.display = 'block';
    }
  }

  hide() {
    // TODO: implement show, with correct animations
    const iframe = FeedbackLoopFrameInjector.getCurrentFeedbackLoopIframe();

    if (iframe) {
      iframe.style.display = 'none';
    }
  }

  close(event: IFrameCloseEvent) {
    const iframe = FeedbackLoopFrameInjector.getCurrentFeedbackLoopIframe();
    iframe?.parentNode?.removeChild(iframe);

    this.#pendingIframeResolver?.(event);

    this.#pendingIframeResolver = undefined;
  }

  #createIframe(
    structure: IFeedbackFormStructure,
    metadata: IFeedbackFormMetadata,
    position = positions.default,
    language: string,
  ) {
    const iframe = createIframe(position);

    const blob = new Blob(
      [generateFormIframeContent({ structure, metadata, language })],
      {
        type: 'text/html',
      },
    );

    iframe.src = URL.createObjectURL(blob);

    return iframe;
  }

  #initIFrame(iframe: HTMLIFrameElement, structure: IFeedbackFormStructure) {
    if (!iframe) {
      return;
    }

    let observer: MutationObserver;

    const events = {
      onMessage: (event: any) => {
        try {
          const json = JSON.parse(event.data);

          switch (json.type) {
            case FORM_CLOSE_EVENT: {
              observer?.disconnect();
              window.removeEventListener('message', events.onMessage);

              exitFadeOut.setupInitialStyles(iframe.style);
              exitFadeOut.startAnimation(iframe.style, () => {
                this.close(json.payload);
              });

              break;
            }
          }
        } catch (e) {}
      },
      onLoad: () => {
        void notifyFormShown(structure.id);

        const root = iframe.contentWindow!.document.getElementById('root');

        const adjustIframeContentHeight = () => {
          iframe.style.height = `${root!.scrollHeight}px`;
        };

        observer = new MutationObserver(() => {
          adjustIframeContentHeight();
        });

        adjustIframeContentHeight();

        enterSlideUpFadeIn.startAnimation(iframe.style);

        observer.observe(root!, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      },
    };

    window.addEventListener('message', events.onMessage);
    iframe.addEventListener('load', events.onLoad);
  }

  #injectFeedbackFormIframe(
    structure: IFeedbackFormStructure,
    metadata: IFeedbackFormMetadata,
    position = positions.default,
    language: string,
  ) {
    const container = document.body;

    if (!container) {
      return;
    }

    const iframe = this.#createIframe(structure, metadata, position, language);
    this.#initIFrame(iframe, structure);

    container.appendChild(iframe);
  }
}
